import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Form, InputRef } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import './PasswordInput.scss';
import NeedHelp from 'components/needHelp/NeedHelp';
import { createSession, showErrorNotification } from 'utils/commonUtils';
import {
  buildLandingPath,
  buildPasswordResetEmailSentPath,
} from 'utils/pathBuilder';
import CarouselContainer from 'components/carouselContainer/CarouselContainer';
import { loginWithPassword, requestResetPassword, updatedTnc } from 'services/loginService';
import { useSession } from 'context/SessionContext';
import { fetchLatestTermsAndConditions } from 'services/termsService';
import TermsAndConditions from 'components/termsAndConditions/TermsAndConditions';

interface PasswordInputFormData {
  currentPassword: string;
}

interface PasswordInputProps {
  type: 'login' | 'reset';
}

const PasswordInput: React.FC<PasswordInputProps> = ({ type }) => {
  const { t, i18n } = useTranslation('password');
  const navigate = useNavigate();
  const [form] = Form.useForm<PasswordInputFormData>();
  const [loading, setLoading] = useState(false);
  const { session, setSession } = useSession();
  const passwordInputRef = useRef<InputRef>(null);

  const [latestTnc, setLatestTnc] = useState<any>(null);
  const [termsVisible, setTermsVisible] = useState(false);
  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%?&*(^)])[A-Za-z\d!@#$%?&*(^)]{8,20}$/;
  const passwordValue = Form.useWatch('currentPassword', form);
  const isRequirementsMet = PASSWORD_REGEX.test(passwordValue || '');
  const location = useLocation();
  const phoneNumber = location.state?.phoneNumber||'';

  const fetchAndProcessTnc = async () => {
    try {
      const response = await fetchLatestTermsAndConditions();
      if (response && response.status === "success") {
        const tncData = response.data;
        if (tncData?.anchor === "Terms-Conditions") {
          setLatestTnc(tncData);
          return tncData;
        }
      }
    } catch (error) {
      console.error("Fetch TnC Error:", error);
    }
    return null;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (passwordInputRef.current) {
        passwordInputRef.current.focus();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchAndProcessTnc();
  }, []);

  const handleConfirm = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      setLoading(true);

      if (type === 'login') {
        const tokenResponse = await loginWithPassword(phoneNumber, values.currentPassword);

        if(!tokenResponse.tnc_required){
          const finalSession = createSession(tokenResponse, session);
          setSession({
            ...finalSession,
            _at: 'password',
            _tc: true
          });
          if (tokenResponse.language) {
            await i18n.changeLanguage(tokenResponse.language);
          }
          navigate(buildLandingPath());
        }else {
          setTermsVisible(true);
        }
      }
      else if (type === 'reset') {
        if (session?._tc) {
          navigate(buildLandingPath());
        } else {
          setTermsVisible(true);
        }
      }

    } catch (error) {
      const backendDetails = (error as any)?.response?.data?.message;
      if (backendDetails && backendDetails.toLowerCase().includes('account locked')) {
        showErrorNotification(t('accountLocked'));
      } else {
        showErrorNotification(t('passwordVerificationFailed'), error as Error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTermsOk = async () => {
      try {
        setLoading(true);
        const values = form.getFieldsValue();
        const tokenResponse = await updatedTnc(phoneNumber, values.currentPassword);
        const finalSession = createSession(tokenResponse, session);
          setSession({
            ...finalSession,
            _at: 'password',
            _tc: true
          });
        setTermsVisible(false);
        navigate(buildLandingPath());
      } catch (error) {
        console.error("Failed to update TnC", error);
        showErrorNotification(t('tncUpdateFailed'), error as Error);
      } finally {
        setLoading(false);
      }

  };

  const handleForgotPassword = async () => {
    try {
      const response = await requestResetPassword();
      const email = response.data.email;

      //this email address should be retrieved from backend
      navigate(buildPasswordResetEmailSentPath(), { state: { email: email } });
    } catch (error) {
      showErrorNotification(t('requestResetPasswordFailed'), error as Error);
    }
  };

  return (
    <CarouselContainer>
      <div className="password-input-form-container">
        <div className="password-input-form-section visible">
          <div className="password-input-title">
            {type === 'login' ? t('enterPassword') : t('enterCurrentPassword')}
          </div>

          <Form form={form} layout="vertical" className="password-input-form" onFinish={handleConfirm}>
            <Form.Item
              name="currentPassword"
              label={t('password')}
              required={false}
              rules={[
                {
                  required: true,
                  message: t('passwordRequired'),
                },
              ]}
            >
              <Input.Password
                ref={passwordInputRef}
                placeholder={t('passwordPlaceholder')}
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
                className="password-input"
              />
            </Form.Item>

            { !isRequirementsMet && (
              <div className="password-requirements">
                {t('passwordRequirements.fullText')}
              </div>
            )}

            <Button
              type="primary"
              block
              htmlType="submit"
              loading={loading}
              className="submit-button"
            >
              {t('confirm')}
            </Button>

            <Button
              type="default"
              block
              onClick={handleForgotPassword}
              color="primary"
              variant="outlined"
              className="forgot-password-button"
            >
              {t('forgotPassword')}
            </Button>
          </Form>

          <NeedHelp />
        </div>
      </div>

      <TermsAndConditions
        visible={termsVisible}
        data={latestTnc}
        onOK={handleTermsOk}
        onClose={() => setTermsVisible(false)}
        closable={false}
        maskClosable={false}
      />
    </CarouselContainer>
  );
};

export default PasswordInput;